import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, LogOut, User } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const statusStyles = {
  nou: "bg-sky-100 text-sky-700",
  confirmat: "bg-indigo-100 text-indigo-700",
  expediat: "bg-amber-100 text-amber-700",
  finalizat: "bg-emerald-100 text-emerald-700",
  anulat: "bg-red-100 text-red-700",
};

const statusLabel = {
  nou: "Nou", confirmat: "Confirmat", expediat: "Expediat", finalizat: "Finalizat", anulat: "Anulat",
};

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/me").then((res) => setOrders(res.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-6 py-12 lg:px-12" data-testid="account-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-sky-100 text-sky-600">
            <User className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-black text-slate-900">{user?.name}</h1>
            <p className="text-slate-500">{user?.email}</p>
          </div>
        </div>
        <Button variant="outline" className="rounded-full" onClick={() => { logout(); navigate("/"); }} data-testid="account-logout">
          <LogOut className="mr-2 h-4 w-4" /> Deconectare
        </Button>
      </div>

      <h2 className="mb-5 mt-12 font-display text-xl font-bold text-slate-900">Comenzile mele</h2>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center" data-testid="account-no-orders">
          <Package className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-slate-500">Nu ai nicio comandă încă.</p>
          <Button className="mt-5 rounded-full bg-sky-600 hover:bg-sky-500" onClick={() => navigate("/produse")}>Începe cumpărăturile</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o._id} className="rounded-2xl border border-slate-100 bg-white p-5" data-testid={`order-${o._id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <p className="text-xs text-slate-400">Comandă #{o._id.slice(-6).toUpperCase()}</p>
                  <p className="text-sm text-slate-600">{new Date(o.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusStyles[o.status] || "bg-slate-100 text-slate-600"}`}>
                  {statusLabel[o.status] || o.status}
                </span>
              </div>
              <div className="mt-4 space-y-1.5">
                {o.items.map((it, idx) => (
                  <div key={idx} className="flex justify-between text-sm text-slate-600">
                    <span>{it.name} × {it.quantity}</span>
                    <span className="font-semibold">{formatPrice(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-between border-t border-slate-100 pt-3">
                <span className="font-semibold text-slate-900">Total</span>
                <span className="font-display font-black text-slate-900">{formatPrice(o.total)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
